import { Component, Input, output, signal } from '@angular/core';
import { DocHierarchyDto } from '@digiwall/goliath-documents/proxy';
import { SubscriptionService } from '@abp/ng.core';
import { Observable } from 'rxjs';
import { DocHierarchyItemComponent } from '../../../components';

@Component({
  selector: 'document-customer-project-hierarchy',
  templateUrl: './customer-project-hierarchy.component.html',
  styleUrl: './customer-project-hierarchy.component.scss',
  imports: [DocHierarchyItemComponent],
})
export class DocCustomerProjectHierarchyComponent {
  @Input() set hierarchyInput(value) {
    this._hierarchy = value;
    this.displayHierarchy();
  }

  hierarchyLoaded = output<DocHierarchyDto>();
  hierarchy = signal<DocHierarchyDto>(null);

  flattenedHierarchy: {
    item: DocHierarchyDto;
    level: number;
    relation: 'prev' | 'self' | 'next';
  }[] = [];
  previousLevels: { depth: number; nodes: DocHierarchyDto[] }[] = [];
  nextLevels: { depth: number; nodes: DocHierarchyDto[] }[] = [];

  private _hierarchy: DocHierarchyDto;

  constructor() {}

  public displayHierarchy() {
    this.hierarchy.set(this._hierarchy);
    this.flattenedHierarchy = this.buildFullHierarchy(this._hierarchy);
    const { previousLevels, nextLevels } = this.groupByDirection(this.flattenedHierarchy);
    this.previousLevels = previousLevels;
    this.nextLevels = nextLevels;
  }

  private buildFullHierarchy(
    root: DocHierarchyDto,
  ): { item: DocHierarchyDto; level: number; relation: 'prev' | 'self' | 'next' }[] {
    const result: { item: DocHierarchyDto; level: number; relation: 'prev' | 'self' | 'next' }[] =
      [];
    const visited = new Set<string>();

    const build = (node: DocHierarchyDto, level: number, relation: 'prev' | 'self' | 'next') => {
      if (!node) return;
      const key = (node.docId || '') + ':' + (node.docRole || '');
      if (visited.has(key)) return;
      visited.add(key);
      node.previousDocs?.forEach(prev => build(prev, level + 1, 'prev'));
      result.push({ item: node, level, relation });
      node.nextDocs?.forEach(next => build(next, level + 1, 'next'));
    };

    build(root, 0, 'self');
    return result;
  }

  groupByDirection(
    flat: {
      item: DocHierarchyDto;
      level: number;
      relation: 'prev' | 'self' | 'next';
    }[],
  ): {
    previousLevels: { depth: number; nodes: DocHierarchyDto[] }[];
    nextLevels: { depth: number; nodes: DocHierarchyDto[] }[];
  } {
    const prevMap = new Map<number, DocHierarchyDto[]>();
    const nextMap = new Map<number, DocHierarchyDto[]>();
    flat.forEach(f => {
      if (f.relation === 'prev') {
        const depth = f.level;
        if (!prevMap.has(depth)) prevMap.set(depth, []);
        prevMap.get(depth)!.push(f.item);
      } else if (f.relation === 'next') {
        const depth = f.level;
        if (!nextMap.has(depth)) nextMap.set(depth, []);
        nextMap.get(depth)!.push(f.item);
      }
    });
    const previousLevels = Array.from(prevMap.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([depth, nodes]) => ({ depth, nodes }));
    const nextLevels = Array.from(nextMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([depth, nodes]) => ({ depth, nodes }));
    return { previousLevels, nextLevels };
  }
}
